import { useEffect, useRef, useState } from 'react'
import './loot-keys-landing.styles.scss';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { ModalAlert } from '@/components/modal/modal.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import FoundCodeItem from '@/components/PLAYER/submit-code/found-code-item.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { getGameActivityFromDb } from '@/utils/getDataFromDb';
import { Route, Routes, useLocation } from 'react-router-dom';
import TransactionView from '@/components/SHARED/transaction-view/transaction-view.component';
import useAnalytics from '@/utils/useAnalytics';
import { gameActivityComplete } from '@/components/PREVIEW/previewGameActivity';

const KEY_LENGTH = 16;
const KEY_GROUP = 4;

const LootKeysLanding = ({
  gameData,
  playerData,
  gameElements
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [keyCode, setKeyCode] = useState('');
  const [activityToView, setActivityToView] = useState(null);
  const [indActivityData, setIndActivityData] = useState(null);
  const { trackEvent, trackException } = useAnalytics();
  const activityRef = useRef();
  const inputRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`Redeem a Loot Key | ${gameData?.gameName}`);
  }, [gameData?.gameName]);

  useEffect(() => {
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = 'Loot Keys'
    }
    getRedeemedKeys();
  }, []);

  function getRedeemedKeys() {
    if (location.pathname.includes('/preview/player/')) {
      const previewActivity = Object.values(gameActivityComplete)
        .filter(a => a.type === 'lootKey' && a.playerId === playerData.playerId);
      const activityObj = {};
      previewActivity.forEach(a => {
        activityObj[a.id] = a;
      });
      activityRef.current = {
        ...activityRef.current,
        ...activityObj
      };
      setActivityToView(Object.values(activityRef.current));
      return;
    }
    getGameActivityFromDb({
      "gameId": gameData.gameId,
      "playerId": playerData.playerId,
      "activityType": "lootKey",
      "once": true,
      "callback": callback,
    });
    function callback(data) {
      activityRef.current = {
        ...activityRef.current,
        ...data
      }
      setActivityToView(Object.values(activityRef.current));
    }
  }

  // strips everything but letters and numbers, uppercases,
  // then rebuilds the code as XXXX-XXXX-XXXX-XXXX
  function formatKeyCode(value) {
    const stripped = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/gi, '')
      .slice(0, KEY_LENGTH);
    const groups = [];
    for (let i = 0; i < stripped.length; i += KEY_GROUP) {
      groups.push(stripped.slice(i, i + KEY_GROUP));
    }
    return groups.join('-');
  }

  function handleKeyCodeChange(e) {
    const input = e.target;
    const rawValue = input.value;
    const selectionStart = input.selectionStart ?? rawValue.length;
    // count the characters (ignoring dashes) that sit before the cursor
    // so the cursor can be put back in the same spot after formatting
    const charsBeforeCursor = rawValue
      .slice(0, selectionStart)
      .replace(/[^A-Za-z0-9]/gi, '')
      .length;
    const formatted = formatKeyCode(rawValue);
    setKeyCode(formatted);

    let newCursor = charsBeforeCursor + Math.floor(
      Math.max(charsBeforeCursor - 1, 0) / KEY_GROUP
    );
    newCursor = Math.min(newCursor, formatted.length);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && keyCode.length === KEY_LENGTH + 3) {
      submitLootKey();
    }
  }

  async function submitLootKey() {
    if (location.pathname.includes('/preview/player/')) {
      setAlertMessage('This is a preview of how the loot key submission process ' +
        'will look for players. In the live game, once you enter a loot key and click "Redeem", ' +
        'your key will be processed and you will receive any rewards or items ' +
        'associated with it. If there are any issues with processing your key, ' +
        'you will receive an alert message with more details.');
      return;
    }

    const formattedCode = formatKeyCode(keyCode);
    if (formattedCode.replace(/-/g, '').length < KEY_LENGTH) {
      setAlertMessage('Please enter your complete loot key in the format XXXX-XXXX-XXXX-XXXX.');
      return;
    }

    setLoading(true);
    const lowerCaseCode = formattedCode.toLowerCase();
    const res = await UseCloudGameTransactions('checkLootKey', {
      'code': lowerCaseCode,
      'gameId': gameData.gameId,
      'playerId': playerData.playerId,
      'playerData': userProfile,
      'gameElements': gameElements,
      'ts': new Date().getTime()
    })
    setLoading(false);
    if (res.error) {
      setAlertMessage('Something went wrong. Please try again soon. (Error: ' + res.error + ')');
      trackException(`Submit Loot Key Error: ${res.error}`);
      return;
    } else if (res.text && res.text === 'code not found') {
      setAlertMessage('Sorry, that loot key is not valid. Please check it and try again.');
      trackEvent(
        'submit_loot_key_invalid',
        'Player',
        `Player submitted an invalid loot key "${formattedCode}" in game ${gameData.gameId}`,
        1,
        null
      )
      return;
    } else if (res.text && res.text === 'no access') {
      setAlertMessage('Sorry, that loot key is not available to you. Please try a different key.');
      trackEvent(
        'submit_loot_key_no_access',
        'Player',
        `Player submitted a loot key without access "${formattedCode}" in game ${gameData.gameId}`,
        1,
        null
      )
      return;
    } else if (res.text && res.text === 'code already claimed') {
      setAlertMessage('Sorry, that loot key has already been redeemed. Each loot key can only be used once.');
      trackEvent(
        'submit_loot_key_already_claimed',
        'Player',
        `Player submitted an already claimed loot key "${formattedCode}" in game ${gameData.gameId}`,
        1,
        null
      )
      return;
    } else if (res.success) {
      setKeyCode('');
      getRedeemedKeys();
      trackEvent(
        'submit_loot_key_success',
        'Player',
        `Player successfully redeemed loot key "${formattedCode}" in game ${gameData.gameId}`,
        1,
        null
      )
      return;
    }
  }

  return (
    <div className={`loot-keys-container ${screenSize}`}>
      <Routes>
        <Route path='' element={
          <div className='loot-keys-content'>
            <div className='g-card'>
              <div className='player-title'>
                <ImageContainer
                  src='/images/icons/lootkey.png'
                  alt='loot keys icon'
                  className='player-title-icon' />
                Loot Keys
              </div>
              <hr />
              <div className='loot-key-card'>
                <div className='code-input'>
                  <div className='input field'>
                    <label>Redeem a Loot Key!</label>
                    <input
                      type='text'
                      id='loot-key-input'
                      ref={inputRef}
                      className='loot-key-field'
                      placeholder='xxxx-xxxx-xxxx-xxxx'
                      autoComplete='off'
                      autoCapitalize='characters'
                      spellCheck='false'
                      inputMode='text'
                      maxLength={KEY_LENGTH + 3}
                      value={keyCode}
                      onChange={handleKeyCodeChange}
                      onKeyDown={handleKeyDown} />
                    <div className='meta'>
                      Enter the code from your loot key. Dashes are added for you.
                    </div>
                    {
                      (loading)
                        ?
                        <div className='buttons'>
                          <button className='g-button' >
                            <ThreeDotsLoader />
                          </button>
                        </div>
                        :
                        <div className='buttons'>
                          <button
                            className={`g-button primary ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                            disabled={keyCode.replace(/-/g, '').length < KEY_LENGTH}
                            onClick={() => submitLootKey()}>
                            Redeem
                          </button>
                        </div>
                    }
                  </div>
                </div>
              </div>
              <div className="g-space-1"></div>
              <h3>Redeemed Loot Keys</h3>
              <hr />
              <div className='redeemed-keys-list'>
                {
                  (activityToView) &&
                  activityToView
                    .sort((a, b) => a.ts < b.ts ? 1 : -1)
                    .map(a => {
                      if (
                        playerData &&
                        playerData.newStuff &&
                        playerData.newStuff.some(item => item.id === a.id)
                      ) return null;
                      return (
                        <div key={a.id} >
                          <FoundCodeItem
                            codeInfo={a}
                            setIndActivityData={setIndActivityData} />
                        </div>
                      )
                    })
                }
              </div>
            </div>
          </div>
        }></Route>
        <Route path=':transPath/*' element={
          (gameData && indActivityData)
            ?
            <div className='g-card'>
              <TransactionView
                playerData={playerData}
                gameElements={gameElements}
                playerList={{ [playerData.playerId]: playerData }}
                hidePlayer={true}
                activityToDisplay={[indActivityData]}
              />
            </div>
            :
            <div></div>
        }></Route>
      </Routes>

      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default LootKeysLanding
